import { ProductImageCropUnit } from '@codezo/graphql';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Minimal shape of a stored crop needed to render it. Mirrors the
 * `ProductImageCrop` GraphQL type but kept structural so any source can pass one.
 */
export interface DisplayCrop {
	x: number;
	y: number;
	width: number;
	height: number;
	/** Persisted for re-editing; not needed to render (the rect already bakes zoom in). */
	zoom?: number | null;
	rotation?: number | null;
	aspectRatio?: string | null;
	/**
	 * `PX` or `PERCENT`, defaulting to `PERCENT`. Widened to `string` because a
	 * quote's or order's frozen crop comes across as a plain String rather than
	 * the catalogue's enum — deliberately, so an old snapshot keeps rendering if
	 * that enum is later extended. Compare against {@link ProductImageCropUnit}.
	 */
	unit?: ProductImageCropUnit | string | null;
}

/** The fields {@link ProductImage} reads off an image. Structural on purpose. */
export interface ProductImageData {
	url?: string | null;
	altText?: string | null;
	name?: string | null;
	crop?: DisplayCrop | null;
}

/**
 * Turn a stored crop rect into inline styles for an absolutely-positioned <img>
 * inside a `position: relative; overflow: hidden` box. Scaling X and Y
 * independently maps the (square, in pixels) crop region onto the square box
 * without distorting the image.
 *
 * Returns null when the crop can't be applied purely with CSS — the caller
 * should fall back to plain `object-cover`. This happens for:
 *  - no crop
 *  - PX units (needs the image's natural size to normalise; our UI emits PERCENT)
 *  - non-zero rotation (would need a transform; the product crop UI has no rotate)
 */
export function cropToStyle(crop?: DisplayCrop | null): CSSProperties | null {
	if (!crop) return null;
	// undefined unit defaults to PERCENT (backend default).
	if (crop.unit && crop.unit !== ProductImageCropUnit.Percent) return null;
	if (crop.rotation) return null;

	const cw = crop.width / 100;
	const ch = crop.height / 100;
	if (!(cw > 0) || !(ch > 0)) return null;

	const cx = crop.x / 100;
	const cy = crop.y / 100;

	return {
		position: 'absolute',
		width: `${100 / cw}%`,
		height: `${100 / ch}%`,
		left: `${(-cx / cw) * 100}%`,
		top: `${(-cy / ch) * 100}%`,
		maxWidth: 'none',
	};
}

/**
 * A frozen product image as the quote and order queries return it: everything
 * {@link ProductImage} renders, plus the flag {@link primaryProductImage} picks
 * on. Components that declare their own item shape should use this rather than
 * narrowing to `{ url }`, which silently drops the crop.
 */
export interface SnapshotProductImage extends ProductImageData {
	isPrimary?: boolean | null;
}

/**
 * The one rule for which of a product's images to show: the primary if one is
 * flagged, else the first as the backend ordered them. Quote lines, order lines
 * and the catalogue all go through this so the same product does not show one
 * photo on a quote and a different one on the order it became.
 */
export function primaryProductImage<T extends { isPrimary?: boolean | null }>(
	images: readonly T[] | null | undefined
): T | null {
	return orderedProductImages(images)[0] ?? null;
}

/**
 * The same images, hero first. Use this wherever a thumbnail and a gallery show
 * the same set: picking the primary for the thumbnail but opening the gallery in
 * raw order means the viewer opens on a different picture than the one clicked.
 */
export function orderedProductImages<T extends { isPrimary?: boolean | null }>(
	images: readonly T[] | null | undefined
): T[] {
	if (!images?.length) return [];
	const primary = images.find((i) => i.isPrimary);
	return primary ? [primary, ...images.filter((i) => i !== primary)] : [...images];
}

interface ProductImageProps {
	/** The image to render; its `crop` is applied automatically. */
	image: ProductImageData | null | undefined;
	/** Classes for the square wrapper (e.g. "aspect-square w-full"). */
	className?: string;
	/** Extra classes for the <img> itself. */
	imgClassName?: string;
	/** Override the alt text (defaults to the image's altText/name). */
	alt?: string;
	/**
	 * Rendered, centred in the same box, when there is no image to show. Lets a
	 * caller hand over its whole "picture or placeholder" branch rather than
	 * repeating the null check at every call site.
	 */
	fallback?: ReactNode;
}

/**
 * The single way to render a product image. Applies the image's stored,
 * non-destructive crop at display time; with no (renderable) crop it behaves
 * like a plain `object-cover` image. Renders its own `relative overflow-hidden`
 * box so the cropped <img> is positioned against it — siblings/badges in the
 * parent are unaffected.
 */
export function ProductImage({ image, className, imgClassName, alt, fallback }: ProductImageProps) {
	const style = cropToStyle(image?.crop);
	const resolvedAlt = alt ?? image?.altText ?? image?.name ?? '';

	/* Lazy and async-decoded: lists render these by the dozen (a rebuilt
	   infinite-scroll list by the hundred) and the urls are full-size originals,
	   so only what is near the viewport should be fetched. In-view images still
	   load immediately. */

	/* No url is the same as no image: a signed url that has expired or was never
	   copied would otherwise render as a broken <img>. */
	if (!image?.url) {
		return (
			<div
				className={cn(
					'relative flex items-center justify-center overflow-hidden',
					className
				)}
			>
				{fallback}
			</div>
		);
	}

	return (
		<div className={cn('relative overflow-hidden', className)}>
			{style ? (
				<img
					src={image.url}
					alt={resolvedAlt}
					className={imgClassName}
					style={style}
					loading="lazy"
					decoding="async"
				/>
			) : (
				<img
					src={image.url}
					alt={resolvedAlt}
					className={cn('h-full w-full object-cover', imgClassName)}
					loading="lazy"
					decoding="async"
				/>
			)}
		</div>
	);
}
